import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash, Star, PaperPlaneRight, Paperclip, X, BookOpen, Compass, Brain, Feather, Users, MagnifyingGlass, Lightbulb, Sparkle, ArrowClockwise, FloppyDisk, CaretUp, CaretDown, Copy, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { PERSONA_STYLES, PROJECT_TYPE_LABELS, STAGE_LABELS, DEV_JOURNEY } from './personas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERSONA_ICONS = {
  novelist: Lightbulb,       // Maya · Innovator
  producer: Compass,         // Alex · Architect (structural)
  editor: Feather,           // Samantha · Wordsmith
  critic: MagnifyingGlass,   // Lawrence · Analyst (was researcher's icon, now his)
  researcher: BookOpen,      // Keith · Researcher (global knowledge)
  mentor: Sparkle,           // Noah · Progress Coach (momentum)
};

const inputCls = "w-full px-4 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 outline-none transition-colors font-sans text-sm";

const daysUntil = (isoStr) => {
  if (!isoStr) return null;
  const ms = new Date(isoStr).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export const CreativeWorkspace = ({ catalogue, status, onStatusChange }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [sidebarView, setSidebarView] = useState('active'); // 'active' | 'archive'
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [personaId, setPersonaId] = useState('novelist');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false); // brief "Saved" indicator
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotView, setSnapshotView] = useState(null); // opened snapshot for read-only preview
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [chatScrollPct, setChatScrollPct] = useState(0);

  const personas = catalogue?.personas || [];
  const smartActions = catalogue?.smart_actions || [];
  const projectTypes = catalogue?.project_types || [];
  const recommendations = catalogue?.recommendations || {};

  const activeProject = useMemo(() => projects.find((p) => p.project_id === activeProjectId) || null, [projects, activeProjectId]);
  const activePersona = personas.find((p) => p.id === personaId);
  const personaStyle = PERSONA_STYLES[personaId] || { accent: 'text-gold', ring: 'border-gold/40', badge: 'bg-gold/10 text-gold' };
  const daysLeft = daysUntil(status?.creative_partner_until);
  const showRenewNudge = typeof daysLeft === 'number' && daysLeft <= 14;

  // Which partners are recommended for the current project's type
  const recommendedIds = useMemo(() => {
    if (!activeProject) return [];
    return recommendations[activeProject.type] || [];
  }, [activeProject, recommendations]);
  const isRecommended = (id) => recommendedIds.includes(id);

  // Show a helpful recommendation when a project's `type` changes (or is first opened)
  const lastRecommendedForRef = useRef(null);
  useEffect(() => {
    if (!activeProject || recommendedIds.length === 0) return;
    const key = `${activeProject.project_id}:${activeProject.type}`;
    if (lastRecommendedForRef.current === key) return;
    lastRecommendedForRef.current = key;
    const suggested = recommendedIds
      .map((id) => personas.find((p) => p.id === id))
      .filter(Boolean);
    if (suggested.length === 0) return;
    const names = suggested.map((p) => p.display_name).join(' and ');
    const roles = suggested.map((p) => `${p.display_name} — ${p.tagline}`).join(' · ');
    toast(
      suggested.length > 1
        ? `For this ${activeProject.type.replace('_', ' ')}, I suggest starting with ${names}. ${roles}`
        : `For this ${activeProject.type.replace('_', ' ')}, I suggest ${names}. ${roles}`,
      { duration: 6000, icon: '💡' }
    );
  }, [activeProject, recommendedIds, personas]);

  const fetchProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/creative-partner/projects`, { withCredentials: true });
      setProjects(r.data.projects || []);
    } catch { /* silent */ }
  }, []);

  const fetchConversations = useCallback(async (projectId) => {
    if (!projectId) { setConversations([]); return null; }
    try {
      const r = await axios.get(`${API}/creative-partner/conversations`, { params: { project_id: projectId }, withCredentials: true });
      const list = r.data.conversations || [];
      setConversations(list);
      return list;
    } catch { return null; }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    if (!convId) { setMessages([]); return; }
    try {
      const r = await axios.get(`${API}/creative-partner/conversations/${convId}`, { withCredentials: true });
      setMessages(r.data.messages || []);
      const conv = r.data.conversation;
      if (conv?.persona_id) setPersonaId(conv.persona_id);
    } catch { /* silent */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/creative-partner/history`, { withCredentials: true });
      setSnapshots(r.data.snapshots || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  // When switching projects, load its conversations AND auto-resume the most
  // recent one so users don't accidentally start a fresh (memoryless) thread.
  useEffect(() => {
    if (!activeProjectId) return;
    (async () => {
      const list = await fetchConversations(activeProjectId);
      if (list && list.length > 0 && !activeConvId) {
        // conversations endpoint returns sorted by updated_at desc — pick [0]
        setActiveConvId(list[0].conversation_id);
      }
    })();
  }, [activeProjectId, fetchConversations]);
  useEffect(() => { fetchMessages(activeConvId); }, [activeConvId, fetchMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (sidebarView === 'history') fetchHistory(); }, [sidebarView, fetchHistory]);
  // Load snapshot count on mount so the History tab shows an accurate badge
  // from the first render (users need to see there are saved items to click).
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Chat scroll ↔ slider sync
  const onChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    setChatScrollPct(maxScroll > 0 ? (el.scrollTop / maxScroll) * 100 : 0);
  };
  const onSliderChange = (pct) => {
    const el = chatScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    el.scrollTop = (pct / 100) * maxScroll;
    setChatScrollPct(pct);
  };

  const handleCreateProject = async (payload) => {
    try {
      const r = await axios.post(`${API}/creative-partner/projects`, payload, { withCredentials: true });
      setProjects((p) => [r.data, ...p]);
      setActiveProjectId(r.data.project_id);
      setShowNewProject(false);
      toast.success('Project created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    }
  };

  const handleUpdateProject = async (projectId, updates) => {
    try {
      const r = await axios.patch(`${API}/creative-partner/projects/${projectId}`, updates, { withCredentials: true });
      setProjects((p) => p.map((x) => x.project_id === projectId ? r.data : x));
      // Brief "Saved" flash — reassures the writer their work persisted
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch {
      toast.error('Could not update');
    }
  };

  const handleSaveToHistory = async (projectId, note = '') => {
    if (!projectId) return;
    setSavingSnapshot(true);
    try {
      const r = await axios.post(`${API}/creative-partner/projects/${projectId}/snapshot`, { note }, { withCredentials: true });
      setSnapshots((s) => [r.data, ...s]);
      // Actionable toast — clicking it jumps to the History tab so the user can
      // see exactly where their save landed (previously it was silent success).
      toast.success('Saved to History', {
        description: 'Tap to view your saved snapshots',
        action: {
          label: 'View History',
          onClick: () => setSidebarView('history'),
        },
        duration: 5000,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSavingSnapshot(false);
    }
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    try {
      await axios.delete(`${API}/creative-partner/history/${snapshotId}`, { withCredentials: true });
      setSnapshots((s) => s.filter((x) => x.snapshot_id !== snapshotId));
    } catch {
      toast.error('Could not delete');
    }
  };

  const handlePersonaSelect = (p) => {
    if (personaId === p.id) return;
    setPersonaId(p.id);
    if (!activeProject) return;
    if (isRecommended(p.id)) {
      toast.success(`Good choice — ${p.display_name} ${p.tagline.toLowerCase()}`, { duration: 4000 });
    } else if (recommendedIds.length > 0) {
      const top = personas.find((x) => x.id === recommendedIds[0]);
      if (top) {
        toast(
          `${p.display_name} is happy to help. For a ${activeProject.type.replace('_', ' ')}, ${top.display_name} may be an even better fit — swap anytime.`,
          { duration: 5000 }
        );
      }
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its conversations?')) return;
    try {
      await axios.delete(`${API}/creative-partner/projects/${projectId}`, { withCredentials: true });
      setProjects((p) => p.filter((x) => x.project_id !== projectId));
      if (activeProjectId === projectId) { setActiveProjectId(null); setActiveConvId(null); }
    } catch {
      toast.error('Could not delete');
    }
  };

  const handleStartConversation = async () => {
    if (!activeProjectId) {
      toast.error('Select or create a project first');
      return;
    }
    try {
      const r = await axios.post(`${API}/creative-partner/conversations`, { project_id: activeProjectId, persona_id: personaId }, { withCredentials: true });
      setConversations((c) => [r.data, ...c]);
      setActiveConvId(r.data.conversation_id);
      setMessages([]);
      setMessageText('');
      setAttachments([]);
    } catch {
      toast.error('Could not start conversation');
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/creative-partner/upload`, fd, { withCredentials: true });
      setAttachments((a) => [...a, r.data]);
      toast.success(`${r.data.filename} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (textToSend) => {
    const text = (textToSend ?? messageText).trim();
    if (!text) { toast.error('Type a message first'); return; }
    if (!activeConvId) {
      // auto-create a conversation if none selected
      if (!activeProjectId) { toast.error('Create or select a project first'); return; }
      await handleStartConversation();
      // wait a tick for state to update; but we need conv id — refetch
      const r = await axios.post(`${API}/creative-partner/conversations`, { project_id: activeProjectId, persona_id: personaId }, { withCredentials: true });
      setConversations((c) => [r.data, ...c]);
      setActiveConvId(r.data.conversation_id);
      // fall through with the new id
      return sendMessageInternal(r.data.conversation_id, text);
    }
    return sendMessageInternal(activeConvId, text);
  };

  const sendMessageInternal = async (convId, text) => {
    setSending(true);
    // Optimistic user message
    const optimistic = { message_id: `tmp_${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setMessageText('');
    try {
      const r = await axios.post(
        `${API}/creative-partner/conversations/${convId}/message`,
        { text, attachments: attachments.map((a) => ({ filename: a.filename, file_id: a.file_id })) },
        { withCredentials: true, timeout: 240000 }
      );
      setMessages((m) => {
        const withoutOpt = m.filter((x) => x.message_id !== optimistic.message_id);
        return [...withoutOpt, r.data.user_message, r.data.assistant_message];
      });
      setAttachments([]);
      fetchConversations(activeProjectId);
    } catch (err) {
      setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
      toast.error(err.response?.data?.detail || 'The partner is thinking too hard — please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const r = await axios.post(
        `${API}/subscriptions/checkout`,
        { lookup_key: 'sub_creative_partner_yearly', origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open checkout');
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-ivory flex flex-col" data-testid="creative-workspace">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 md:px-10 shrink-0">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/')} className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold" data-testid="cp-back-dashboard">
            <ArrowLeft size={14} weight="duotone" className="mr-2" />
            Dashboard
          </Button>
          <h1 className="text-lg font-serif font-medium tracking-tight text-ivory">Creative Partner</h1>
        </div>
        <div className="flex items-center gap-3">
          {showRenewNudge && (
            <button onClick={handleRenew} disabled={renewing} className="hidden md:inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.28em] text-rose-300 border border-rose-400/40 bg-rose-400/5 px-3 py-2 rounded-md hover:bg-rose-400/10" data-testid="renew-nudge-btn">
              <ArrowClockwise size={12} weight="duotone" />
              Renew ({daysLeft}d left)
            </button>
          )}
          <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hidden md:inline">
            {status?.admin_complimentary
              ? 'Host · Complimentary'
              : `Member · until ${status?.creative_partner_until ? new Date(status.creative_partner_until).toLocaleDateString() : '—'}`}
          </span>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[300px_1fr] xl:grid-cols-[300px_1fr_320px] overflow-hidden">
        {/* Left: Projects sidebar */}
        <aside className="border-r border-border/60 overflow-y-auto bg-card/20 flex flex-col">
          <div className="p-4 border-b border-border/60 flex items-center justify-between">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Projects</h2>
            <div className="flex items-center gap-2">
              {savedFlash && (
                <span className="text-[9px] font-medium uppercase tracking-[0.28em] text-emerald-300 animate-fade-in" data-testid="saved-flash">
                  · Saved
                </span>
              )}
              <button
                onClick={() => setShowNewProject((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.24em] text-charcoal bg-gold hover:bg-gold-dark transition-colors"
                title="Create your first project"
                data-testid="new-project-btn"
              >
                <Plus size={14} weight="bold" />
                <span>Start here</span>
              </button>
            </div>
          </div>

          {/* Active / Archive / History tabs — bigger click targets for clarity */}
          <div className="grid grid-cols-3 border-b border-border/60 shrink-0 bg-charcoal/40" data-testid="sidebar-tabs">
            {[
              { key: 'active', label: 'Projects', count: projects.filter((p) => !p.is_archived).length },
              { key: 'archive', label: 'Archive', count: projects.filter((p) => p.is_archived).length },
              { key: 'history', label: 'History', count: snapshots.length },
            ].map((t) => {
              const isActive = sidebarView === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setSidebarView(t.key)}
                  className={`px-2 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all border-b-2 flex items-center justify-center gap-1.5 ${isActive ? 'border-gold text-gold bg-charcoal/60' : 'border-transparent text-ash hover:text-ivory hover:bg-stone/20'}`}
                  data-testid={`sidebar-tab-${t.key}`}
                >
                  <span>{t.label}</span>
                  {t.count > 0 && (
                    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold ${isActive ? 'bg-gold text-charcoal' : 'bg-stone/60 text-bone'}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* History-mode hint bar so users know exactly what History shows */}
          {sidebarView === 'history' && (
            <div className="px-4 py-2 text-[10px] text-ash bg-stone/20 border-b border-border/40 leading-relaxed">
              <span className="text-gold font-medium">Snapshots</span> are checkpoints you saved with <span className="text-gold">Save to History</span>. They&apos;re read-only — your live chats stay in <span className="text-gold">Projects</span>.
            </div>
          )}

          {showNewProject && sidebarView !== 'history' && (
            <NewProjectForm projectTypes={projectTypes} onCancel={() => setShowNewProject(false)} onCreate={handleCreateProject} />
          )}

          <div className="flex-1 overflow-y-auto">
            {sidebarView === 'history' ? (
              snapshots.length === 0 ? (
                <div className="p-6 text-center text-sm text-ash font-sans" data-testid="sidebar-empty-history">
                  <BookOpen size={22} weight="duotone" className="text-gold mx-auto mb-2" />
                  No saved snapshots yet. Click <span className="text-gold">Save to History</span> on any project to capture a snapshot.
                </div>
              ) : (
                snapshots.map((s) => (
                  <button
                    key={s.snapshot_id}
                    onClick={() => setSnapshotView(s)}
                    className="w-full text-left px-4 py-3 border-b border-border/40 hover:bg-stone/30 transition-colors"
                    data-testid={`snapshot-${s.snapshot_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-ivory text-sm truncate">{s.name}</span>
                      <span className="text-[9px] uppercase tracking-[0.22em] text-gold shrink-0 ml-2">{STAGE_LABELS[s.stage] || s.stage}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-ash">
                      <span>{new Date(s.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      <span>·</span>
                      <span>{s.message_count} msgs</span>
                    </div>
                    {s.note && <p className="text-[11px] text-bone italic mt-1 truncate">&ldquo;{s.note}&rdquo;</p>}
                  </button>
                ))
              )
            ) : (() => {
              const filtered = projects.filter((p) => sidebarView === 'archive' ? p.is_archived : !p.is_archived);
              if (filtered.length === 0 && !showNewProject) {
                return (
                  <div className="p-6 text-center text-sm text-ash font-sans" data-testid={`sidebar-empty-${sidebarView}`}>
                    <BookOpen size={22} weight="duotone" className="text-gold mx-auto mb-2" />
                    {sidebarView === 'archive'
                      ? 'No archived projects yet. Anything you archive lands here — one click to reopen.'
                      : (<>No projects yet. Click <span className="text-gold">+ Start here</span> to create your first.</>)}
                  </div>
                );
              }
              return filtered.map((p) => (
                <button
                  key={p.project_id}
                  onClick={() => {
                    // If they're clicking a DIFFERENT project, clear the active
                    // conversation so the useEffect auto-picks the most recent
                    // one for that project. If they click the current project,
                    // leave state alone (no accidental churn).
                    if (activeProjectId !== p.project_id) {
                      setActiveProjectId(p.project_id);
                      setActiveConvId(null);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 border-b border-border/40 hover:bg-stone/30 transition-colors ${activeProjectId === p.project_id ? 'bg-stone/40 border-l-2 border-l-gold' : ''}`}
                  data-testid={`project-${p.project_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-ivory text-sm truncate">{p.name}</span>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {p.is_favorite && <Star size={12} weight="fill" className="text-gold" />}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-ash">
                    <span>{PROJECT_TYPE_LABELS[p.type] || p.type}</span>
                    <span>·</span>
                    <span className="text-gold">{STAGE_LABELS[p.stage] || p.stage}</span>
                    <span className="text-ash/60 ml-auto normal-case tracking-normal">{new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ));
            })()}
          </div>
        </aside>

        {/* Center: Chat + persona picker */}
        <main className="flex flex-col overflow-hidden">
          {/* Persona strip */}
          <div className="border-b border-border/60 px-6 py-3 flex items-center gap-2 overflow-x-auto shrink-0">
            {personas.map((p) => {
              const Icon = PERSONA_ICONS[p.id] || Sparkle;
              const st = PERSONA_STYLES[p.id] || {};
              const active = personaId === p.id;
              const rec = isRecommended(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => handlePersonaSelect(p)}
                  className={`relative shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${active ? `${st.badge} ${st.ring} border` : `${rec ? 'border-gold/50 text-ivory' : 'border-transparent text-ash'} hover:text-ivory`}`}
                  title={rec ? `Suggested for this project — ${p.tagline}` : p.tagline}
                  data-testid={`persona-${p.id}`}
                >
                  <Icon size={14} weight="duotone" />
                  <span className="font-serif">{p.display_name}</span>
                  <span className="hidden md:inline text-[10px] opacity-70">· {p.title.replace('The ', '')}</span>
                  {rec && !active && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-gold" title="Recommended for this project" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Chat area with a large scroll slider on the right */}
          <div className="relative flex-1 flex overflow-hidden">
            <div
              ref={chatScrollRef}
              onScroll={onChatScroll}
              className="flex-1 overflow-y-auto px-6 py-6 cp-chat-scroll"
              data-testid="chat-area"
            >
              {!activeProject ? (
                <EmptyChatState title="Start with a project" body="Create or pick a project from the left. Your creative partner will remember every step." />
              ) : messages.length === 0 ? (
                <EmptyChatState title={`Chat with ${activePersona?.display_name || 'Maya'}`} body={activePersona?.tagline || ''} personaStyle={personaStyle} />
              ) : (
                <div className="max-w-3xl mx-auto space-y-6" data-testid="chat-messages">
                  {messages.map((m) => (
                    <MessageBubble key={m.message_id} msg={m} persona={activePersona} personaStyle={personaStyle} projectName={activeProject?.name} />
                  ))}
                  {sending && <TypingBubble persona={activePersona} personaStyle={personaStyle} />}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            {/* Large vertical scroll slider — appears only when the chat overflows */}
            {activeProject && messages.length > 3 && (
              <ChatScrollSlider
                percent={chatScrollPct}
                onChange={onSliderChange}
                onJumpTop={() => onSliderChange(0)}
                onJumpBottom={() => onSliderChange(100)}
              />
            )}
          </div>

          {/* Smart actions + Save-to-History (bottom, always in reach) */}
          {activeProject && (
            <div className="border-t border-border/60 px-6 py-3 shrink-0">
              <div className="max-w-3xl mx-auto flex items-center gap-2">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1 min-w-0">
                  {smartActions.map((a) => {
                    // Variant styling:
                    //   • primary — filled gold pill (marquee CTA)
                    //   • accent  — outlined gold pill (sibling hero, e.g.
                    //               Cheerleader for morale + course-check)
                    //   • default — pale bordered chip (analytical tools)
                    const isPrimary = a.variant === 'primary';
                    const isAccent  = a.variant === 'accent';
                    const cls = isPrimary
                      ? 'shrink-0 text-[10px] font-semibold uppercase tracking-[0.24em] bg-gold text-charcoal hover:bg-gold-dark border border-gold px-3.5 py-2 rounded-md transition-colors disabled:opacity-40 inline-flex items-center gap-1.5'
                      : isAccent
                        ? 'shrink-0 text-[10px] font-semibold uppercase tracking-[0.24em] text-gold hover:bg-gold/10 border border-gold px-3.5 py-2 rounded-md transition-colors disabled:opacity-40 inline-flex items-center gap-1.5'
                        : 'shrink-0 text-[10px] font-medium uppercase tracking-[0.24em] text-bone hover:text-gold border border-border hover:border-gold px-3 py-2 rounded-md transition-colors disabled:opacity-40';
                    const titleCopy = isPrimary
                      ? 'Begin drafting the actual manuscript using everything we\'ve discussed'
                      : isAccent
                        ? 'A warm pat on the back plus a gentle check that the story is still on track'
                        : undefined;
                    return (
                      <button
                        key={a.id}
                        onClick={() => sendMessage(a.prompt)}
                        disabled={sending}
                        className={cls}
                        title={titleCopy}
                        data-testid={`smart-action-${a.id}`}
                      >
                        {(isPrimary || isAccent) && <Sparkle size={11} weight={isPrimary ? 'fill' : 'duotone'} />}
                        {a.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handleSaveToHistory(activeProject.project_id)}
                  disabled={savingSnapshot}
                  className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.24em] bg-gold text-charcoal hover:bg-gold-dark px-3 py-2 rounded-md transition-colors disabled:opacity-50"
                  title="Snapshot this project into History"
                  data-testid="cp-save-btn-bottom"
                >
                  <FloppyDisk size={12} weight="duotone" />
                  {savingSnapshot ? 'Saving…' : 'Save to History'}
                </button>
              </div>
            </div>
          )}

          {/* Composer */}
          {activeProject && (
            <div className="border-t border-border px-6 py-4 shrink-0 bg-card/40">
              <div className="max-w-3xl mx-auto">
                {attachments.length === 0 && (
                  <div className="flex items-center gap-2 mb-2 text-[11px] text-ash" data-testid="cp-attach-hint">
                    <Paperclip size={11} weight="duotone" className="text-gold" />
                    <span>
                      Attach a manuscript for context —
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || sending}
                        aria-label="Browse files to attach"
                        className="underline text-gold hover:text-gold-dark ml-1 disabled:opacity-40"
                        data-testid="cp-attach-hint-link"
                      >
                        {uploading ? 'uploading…' : 'browse .txt / .md / .docx / .pdf / .fdx / .fountain'}
                      </button>
                    </span>
                  </div>
                )}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid="attachment-chips">
                    {attachments.map((a) => (
                      <span key={a.file_id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-gold/10 text-gold border border-gold/30">
                        <Paperclip size={11} />
                        {a.filename}
                        <button onClick={() => setAttachments((prev) => prev.filter((x) => x.file_id !== a.file_id))} className="hover:text-rose-300"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input ref={fileInputRef} type="file" accept=".txt,.md,.docx,.pdf,.fdx,.fountain" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="cp-file-input" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || sending}
                    className="px-3 py-3 rounded-lg border border-gold/50 text-gold hover:bg-gold/10 hover:border-gold transition-colors disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
                    title="Attach a manuscript file"
                    data-testid="cp-attach-btn"
                  >
                    <Paperclip size={16} weight="duotone" />
                    <span className="text-[10px] font-medium uppercase tracking-[0.24em] hidden sm:inline">
                      {uploading ? 'Uploading…' : 'Attach'}
                    </span>
                  </button>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    rows={2}
                    placeholder={`Message ${activePersona?.display_name || 'Maya'}… (Shift+Enter for new line)`}
                    className={`${inputCls} flex-1 resize-none`}
                    disabled={sending}
                    data-testid="cp-message-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={sending || !messageText.trim()}
                    className="p-3 rounded-lg bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Send"
                    data-testid="cp-send-btn"
                  >
                    <PaperPlaneRight size={16} weight="fill" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Right: project detail (xl only) */}
        {activeProject && (
          <aside className="hidden xl:flex flex-col border-l border-border/60 bg-card/20 overflow-y-auto">
            <ProjectDetailPanel
              project={activeProject}
              projectTypes={projectTypes}
              onUpdate={(u) => handleUpdateProject(activeProject.project_id, u)}
              onDelete={() => handleDeleteProject(activeProject.project_id)}
              onSaveToHistory={(note) => handleSaveToHistory(activeProject.project_id, note)}
              savingSnapshot={savingSnapshot}
              conversations={conversations}
              activeConvId={activeConvId}
              setActiveConvId={setActiveConvId}
              onNewConversation={handleStartConversation}
              personaId={personaId}
            />
          </aside>
        )}
      </div>

      {/* Snapshot viewer modal */}
      {snapshotView && (
        <SnapshotViewer
          snapshot={snapshotView}
          onClose={() => setSnapshotView(null)}
          onOpenProject={() => {
            const target = projects.find((p) => p.project_id === snapshotView.project_id);
            if (target) {
              setActiveProjectId(target.project_id);
              setSidebarView(target.is_archived ? 'archive' : 'active');
              setSnapshotView(null);
            } else {
              toast.error('Original project has been deleted');
            }
          }}
          onDelete={() => { handleDeleteSnapshot(snapshotView.snapshot_id); setSnapshotView(null); }}
        />
      )}
    </div>
  );
};

// ================ Sub-components ================

const NewProjectForm = ({ projectTypes, onCancel, onCreate }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('novel');
  const [description, setDescription] = useState('');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; onCreate({ name: name.trim(), type, description: description.trim(), stage: 'idea' }); }}
      className="p-4 border-b border-border/60 space-y-3 bg-stone/20"
      data-testid="new-project-form"
    >
      <input className={inputCls} placeholder="Project name *" value={name} onChange={(e) => setName(e.target.value)} required data-testid="new-project-name" />
      <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="new-project-type">
        {projectTypes.map((t) => (<option key={t} value={t}>{PROJECT_TYPE_LABELS[t] || t}</option>))}
      </select>
      <textarea className={`${inputCls} min-h-[70px] resize-none`} placeholder="Short description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-[10px] font-medium uppercase tracking-[0.24em] text-ash px-3 py-2 hover:text-ivory">Cancel</button>
        <button type="submit" className="text-[10px] font-medium uppercase tracking-[0.24em] bg-gold text-charcoal hover:bg-gold-dark px-4 py-2 rounded-md" data-testid="new-project-submit">Create</button>
      </div>
    </form>
  );
};

const ProjectDetailPanel = ({ project, projectTypes, onUpdate, onDelete, onSaveToHistory, savingSnapshot, conversations, activeConvId, setActiveConvId, onNewConversation, personaId }) => {
  // Auto-save inputs (debounced)
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const nameTimer = useRef(null);
  const descTimer = useRef(null);

  // Sync when a different project is opened
  useEffect(() => {
    setName(project.name);
    setDescription(project.description || '');
  }, [project.project_id, project.name, project.description]);

  const scheduleSave = (timerRef, key, value, delay = 800) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onUpdate({ [key]: value });
    }, delay);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <input
            className="flex-1 bg-transparent border-none outline-none text-xl font-serif font-normal text-ivory leading-tight focus:bg-stone/30 rounded px-1 py-0.5 -mx-1 transition-colors"
            value={name}
            onChange={(e) => { setName(e.target.value); scheduleSave(nameTimer, 'name', e.target.value); }}
            placeholder="Project name"
            data-testid="cp-name-input"
          />
          <button onClick={() => onUpdate({ is_favorite: !project.is_favorite })} className="shrink-0 p-1.5 rounded-md text-ash hover:text-gold" title="Favourite" data-testid="cp-favorite-toggle">
            <Star size={16} weight={project.is_favorite ? 'fill' : 'regular'} className={project.is_favorite ? 'text-gold' : ''} />
          </button>
        </div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-ash">
          {PROJECT_TYPE_LABELS[project.type] || project.type}
          {project.is_archived && <span className="ml-2 text-rose-300">· Archived</span>}
        </p>
      </div>

      {/* Development journey */}
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-3">Development stage</p>
        <div className="grid grid-cols-3 gap-1.5" data-testid="dev-journey-grid">
          {DEV_JOURNEY.map((s) => {
            const active = project.stage === s;
            return (
              <button
                key={s}
                onClick={() => onUpdate({ stage: s })}
                className={`text-[10px] font-medium uppercase tracking-[0.18em] px-2 py-2 rounded-md border transition-colors ${active ? 'bg-gold text-charcoal border-gold' : 'text-ash border-border hover:border-gold hover:text-ivory'}`}
                data-testid={`stage-${s}`}
              >
                {STAGE_LABELS[s] || s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Description — always visible with auto-save */}
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-2">Description · auto-saves</p>
        <textarea
          className={`${inputCls} min-h-[100px] resize-y`}
          value={description}
          onChange={(e) => { setDescription(e.target.value); scheduleSave(descTimer, 'description', e.target.value); }}
          placeholder="Describe your project — characters, world, deadline, notes. Auto-saves as you type."
          data-testid="cp-description-input"
        />
      </div>

      {/* Conversations — clearly labelled so new users know these are past chats they can resume */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Past chats · click to resume</p>
          <button onClick={onNewConversation} className="text-[10px] font-medium uppercase tracking-[0.24em] text-gold hover:text-gold-dark inline-flex items-center gap-1" data-testid="new-conv-btn">+ New chat</button>
        </div>
        <p className="text-[10px] text-ash mb-3 leading-relaxed italic">
          Your assistant remembers everything in a chat. Click a past chat to continue where you left off — it will pick back up with full memory.
        </p>
        <div className="space-y-1.5">
          {conversations.length === 0 ? (
            <p className="text-xs text-ash">No chats yet — send a message below to start.</p>
          ) : conversations.map((c) => {
            const s = PERSONA_STYLES[c.persona_id] || {};
            const isCurrent = activeConvId === c.conversation_id;
            return (
              <button key={c.conversation_id} onClick={() => setActiveConvId(c.conversation_id)} className={`w-full text-left px-3 py-2 rounded-md text-xs transition-all ${isCurrent ? 'bg-stone/60 border-l-2 border-l-gold pl-3' : 'hover:bg-stone/40 border-l-2 border-l-transparent'}`} data-testid={`conv-${c.conversation_id}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${s.badge?.split(' ')[0] || 'bg-gold/40'}`} />
                  <span className={`${s.accent || 'text-gold'} font-medium truncate`}>{c.title}</span>
                  {isCurrent && <span className="ml-auto text-[9px] uppercase tracking-[0.2em] text-gold shrink-0">Current</span>}
                </div>
                <p className="text-[10px] text-ash mt-0.5">{new Date(c.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Explicit Save-to-History — clarify what this button actually does */}
      <div>
        <button
          onClick={() => onSaveToHistory('')}
          disabled={savingSnapshot}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md text-xs font-medium uppercase tracking-[0.24em] px-4 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 transition-colors"
          data-testid="cp-save-btn"
        >
          <FloppyDisk size={14} weight="duotone" />
          {savingSnapshot ? 'Saving…' : 'Save Snapshot to History'}
        </button>
        <p className="text-[10px] text-ash mt-2 leading-relaxed">
          Saves a read-only checkpoint of this project&apos;s current state.
          Your live chats above are <span className="text-gold">already auto-saved</span> —
          this button is for milestone archives you might want to look back on later,
          viewable in the <span className="text-gold">History</span> tab.
        </p>
      </div>

      {/* Danger zone */}
      <div className="border-t border-border/60 pt-6">
        <div className="flex gap-2">
          <button
            onClick={() => onUpdate({ is_archived: !project.is_archived })}
            className="flex-1 text-[10px] font-medium uppercase tracking-[0.24em] text-ash border border-border hover:border-gold hover:text-gold py-2 px-3 rounded-md"
            data-testid="cp-archive-toggle"
          >
            {project.is_archived ? 'Move to Active' : 'Move to Archive'}
          </button>
          <button onClick={onDelete} className="text-[10px] font-medium uppercase tracking-[0.24em] text-rose-400 border border-rose-400/40 hover:bg-rose-400/10 py-2 px-3 rounded-md" data-testid="cp-delete-project">
            <Trash size={12} weight="duotone" />
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyChatState = ({ title, body, personaStyle }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="chat-empty">
    <Sparkle size={40} weight="duotone" className={`${personaStyle?.accent || 'text-gold'} mb-6 opacity-60`} />
    <h3 className="text-2xl md:text-3xl font-serif font-light text-ivory mb-3">{title}</h3>
    <p className="text-sm text-bone font-sans max-w-md leading-relaxed">{body}</p>
  </div>
);

const MessageBubble = ({ msg, persona, personaStyle, projectName }) => {
  const isUser = msg.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid={`msg-user-${msg.message_id}`}>
        <div className="max-w-[80%] bg-gold/10 border border-gold/30 rounded-lg px-4 py-3 text-ivory font-sans text-sm leading-relaxed whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.content);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and Ctrl+C');
    }
  };

  const handleDownload = () => {
    const safeProject = (projectName || 'creative-partner').replace(/[^a-z0-9-]+/gi, '_').slice(0, 50);
    const stamp = new Date(msg.created_at || Date.now()).toISOString().slice(0, 10);
    const filename = `${safeProject}-${persona?.display_name || 'partner'}-${stamp}.txt`;
    const blob = new Blob([msg.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  return (
    <div className="flex justify-start gap-3" data-testid={`msg-assistant-${msg.message_id}`}>
      <div className={`shrink-0 h-8 w-8 rounded-full ${personaStyle?.badge || 'bg-gold/10 text-gold'} flex items-center justify-center font-serif text-sm`}>
        {persona?.display_name?.[0] || 'M'}
      </div>
      <div className="max-w-[80%] flex-1 min-w-0">
        <p className={`text-[10px] uppercase tracking-[0.28em] mb-1 ${personaStyle?.accent || 'text-gold'} font-medium`}>{persona?.display_name || 'Partner'}</p>
        <div className="bg-stone/40 border border-border rounded-lg px-4 py-3 text-ivory font-sans text-sm leading-relaxed whitespace-pre-wrap">
          {msg.content}
        </div>
        {/* Per-message copy + download — grab a chapter without scrolling anywhere */}
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.24em] text-ash hover:text-gold border border-border hover:border-gold px-2.5 py-1.5 rounded-md transition-colors"
            data-testid={`msg-copy-${msg.message_id}`}
          >
            <Copy size={11} weight="duotone" /> Copy
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.24em] text-ash hover:text-gold border border-border hover:border-gold px-2.5 py-1.5 rounded-md transition-colors"
            data-testid={`msg-download-${msg.message_id}`}
          >
            <DownloadSimple size={11} weight="duotone" /> Download .txt
          </button>
        </div>
      </div>
    </div>
  );
};

const TypingBubble = ({ persona, personaStyle }) => (
  <div className="flex justify-start gap-3" data-testid="chat-typing">
    <div className={`shrink-0 h-8 w-8 rounded-full ${personaStyle?.badge || 'bg-gold/10 text-gold'} flex items-center justify-center font-serif text-sm`}>
      {persona?.display_name?.[0] || 'M'}
    </div>
    <div>
      <p className={`text-[10px] uppercase tracking-[0.28em] mb-1 ${personaStyle?.accent || 'text-gold'} font-medium`}>{persona?.display_name || 'Partner'}</p>
      <div className="bg-stone/40 border border-border rounded-lg px-4 py-3">
        <div className="flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-gold/60 animate-pulse" />
          <span className="h-1.5 w-1.5 rounded-full bg-gold/60 animate-pulse [animation-delay:0.2s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-gold/60 animate-pulse [animation-delay:0.4s]" />
        </div>
      </div>
    </div>
  </div>
);

// Large vertical slider on the right of the chat area — lets writers scrub
// through long conversations at a glance. Implemented as a rotated horizontal
// range input for reliable cross-browser support.
const ChatScrollSlider = ({ percent, onChange, onJumpTop, onJumpBottom }) => (
  <div className="shrink-0 w-14 border-l border-border/60 bg-card/30 flex flex-col items-center py-3" data-testid="chat-scroll-slider">
    <button
      onClick={onJumpTop}
      className="h-8 w-8 rounded-full border border-border text-gold hover:bg-gold hover:text-charcoal transition-colors flex items-center justify-center"
      title="Jump to top"
      data-testid="chat-jump-top"
    >
      <CaretUp size={14} weight="bold" />
    </button>
    <div className="flex-1 w-full flex items-center justify-center my-2 relative">
      {/* Rotated horizontal slider — reliable in every browser */}
      <input
        type="range"
        min={0}
        max={100}
        step={0.5}
        value={percent}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="cp-chat-slider absolute origin-center"
        style={{ transform: 'rotate(90deg)', width: '75%' }}
        aria-label="Chat scroll position"
        data-testid="chat-slider-input"
      />
    </div>
    <button
      onClick={onJumpBottom}
      className="h-8 w-8 rounded-full border border-border text-gold hover:bg-gold hover:text-charcoal transition-colors flex items-center justify-center"
      title="Jump to bottom"
      data-testid="chat-jump-bottom"
    >
      <CaretDown size={14} weight="bold" />
    </button>
  </div>
);

// Read-only snapshot preview shown from the History tab.
const SnapshotViewer = ({ snapshot, onClose, onOpenProject, onDelete }) => (
  <div className="fixed inset-0 z-50 bg-charcoal/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="snapshot-viewer">
    <div className="bg-background border border-border max-w-2xl w-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between p-6 border-b border-border/60">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">History snapshot</p>
          <h3 className="text-2xl font-serif font-normal text-ivory">{snapshot.name}</h3>
          <p className="text-xs text-ash mt-1">{new Date(snapshot.created_at).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}</p>
        </div>
        <button onClick={onClose} className="text-ash hover:text-ivory" data-testid="snapshot-close"><X size={20} /></button>
      </div>
      <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div><p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-1">Stage</p><p className="text-gold">{snapshot.stage}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-1">Conversations</p><p className="text-ivory">{snapshot.conversation_count}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-1">Messages</p><p className="text-ivory">{snapshot.message_count}</p></div>
        </div>
        {snapshot.description && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-2">Description at time of save</p>
            <p className="text-sm text-bone font-sans leading-relaxed whitespace-pre-wrap">{snapshot.description}</p>
          </div>
        )}
        {snapshot.note && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-ash mb-2">Note</p>
            <p className="text-sm text-ivory italic">&ldquo;{snapshot.note}&rdquo;</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between p-5 border-t border-border/60 gap-3">
        <button onClick={onDelete} className="text-[10px] font-medium uppercase tracking-[0.24em] text-rose-400 hover:bg-rose-400/10 py-2 px-3 rounded-md inline-flex items-center gap-2" data-testid="snapshot-delete">
          <Trash size={12} weight="duotone" /> Delete snapshot
        </button>
        <button onClick={onOpenProject} className="text-[10px] font-medium uppercase tracking-[0.24em] bg-gold text-charcoal hover:bg-gold-dark py-2.5 px-5 rounded-md" data-testid="snapshot-open-project">
          Open the project
        </button>
      </div>
    </div>
  </div>
);
